import { ThemeProvider } from '@mui/material/styles';
import { createContext, useContext } from 'react';
import { track, AppUrls } from '../logic';
import { createAppTheme } from '../logic/theme';
import { useGetConfig } from '../logic/useApiUnauthedQuery';
import { PortalDomainOrg } from '../types';
import { ErrorDialog } from './shared';
import { LoadingScreen } from './shared/LoadingScreen';

//Stuff tacked onto the organization context is so that they're accessible in other components without calling getConfig again.
type OrganizationContextType = PortalDomainOrg & {
  auth0TicketId?: string;
  companyIdOrderIdHash?: string;
  userAcceptedTerms?: boolean;
  outageBanner?: string | null;
}

//The null as any is a trick to avoid having to make everything that uses the context check for null.
export const OrganizationContext = createContext<OrganizationContextType>(null as any);

//Use this if you want to allow the usage of organization outside of the provider.
export function useOrganizationUnsafe(): OrganizationContextType | null {
  return useContext(OrganizationContext)
}

export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context 
}

export function OrganizationProvider({ children }: {
	children: JSX.Element;
}) {
  const { isLoading: loading, data, error, isError } = useGetConfig({
    onSuccess: (data) => {
      if (data.redirectTo) {
        window.location.assign(data.redirectTo)
      }
    },
    useErrorBoundary: false
  })

  if (loading) {
    return <LoadingScreen />
  }

  if (isError) {
    if (error.statusCode === 422) {
      track('error', '422 Mapping already exists (used invite link)')
      error.message = 'This invitation is not valid. It may have already been used or is expired. Please request a new invitation.'
    } else {
      track('error', 'useGetConfig', {}, {
        message: JSON.stringify(error),
        component: 'App',
      })
    }

    const redirectToRequestInvite = () => window.location.assign(AppUrls.requestInvite())

    return (
      <ErrorDialog
        error={error}
        onTryAgain={redirectToRequestInvite}
        waitOnAuth0={false}
        tryAgainText='Request Invitation'
      />
    )
  }

  const organization = data.org

  const theme = createAppTheme(organization)

  return (
    <OrganizationContext.Provider value={{...organization, auth0TicketId: data.auth0TicketId, companyIdOrderIdHash: data.companyIdOrderIdHash, userAcceptedTerms: data.userAcceptedTerms, outageBanner: data.outageBanner ?? null}}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </OrganizationContext.Provider>
  )
}
